import { EAccessScope } from "../../shared/oversight-core/enums/access-scope";
import { IAccessGrant } from "../../shared/oversight-core/interfaces/access-grant";
import { IAccessTarget } from "../../shared/oversight-core/interfaces/access-target";
import { IOrgUser } from "../../shared/oversight-core/interfaces/org-user";
import { IOrganizationUserView } from "../../shared/oversight-core/interfaces/organization-user-view";
import { IRequestedGrant } from "../../shared/oversight-core/interfaces/requested-grant";
import { IScopeNodeView } from "../../shared/oversight-core/interfaces/scope-node-view";
import { IUserScopeGrantDetailView } from "../../shared/oversight-core/interfaces/user-scope-grant-detail-view";

const toAccessScope = (type: string): EAccessScope => {
  const normalized = type?.toLowerCase();
  return (
    Object.values(EAccessScope).find((s) => s === normalized) ??
    EAccessScope.SITE
  );
};

// Builds the selectable targets from the flat scope node list, walking parentIdentity up to
// the root (null) for each node's breadcrumb.
export const toAccessTargets = (nodes: IScopeNodeView[]): IAccessTarget[] => {
  const nodesById = new Map(nodes.map((n) => [n.identity, n]));

  return nodes.map((node) => {
    const ancestors: IScopeNodeView[] = [];
    let parent = node.parentIdentity
      ? nodesById.get(node.parentIdentity)
      : undefined;
    // The guard stops a malformed parent cycle from looping forever.
    while (parent && ancestors.length < nodes.length) {
      ancestors.unshift(parent);
      parent = parent.parentIdentity
        ? nodesById.get(parent.parentIdentity)
        : undefined;
    }

    return {
      identity: node.identity,
      parentIdentity: node.parentIdentity,
      scope: toAccessScope(node.type),
      scopeName: node.name,
      path: ancestors.map((a) => a.name),
      ancestorIds: ancestors.map((a) => a.identity),
      isRetired: !node.isActive,
    };
  });
};

// Grants carry their node's name and type; the tree, when given, adds the breadcrumb.
export const toAccessGrant = (
  grant: IUserScopeGrantDetailView,
  targetsById?: Map<string, IAccessTarget>,
): IAccessGrant => {
  const target = targetsById?.get(grant.scopeNodeId);

  return {
    identity: grant.scopeNodeId,
    parentIdentity: target?.parentIdentity,
    scope: toAccessScope(grant.scopeNodeType),
    scopeName: grant.scopeNodeName,
    path: target?.path ?? [],
    ancestorIds: target?.ancestorIds ?? [],
    isRetired: target?.isRetired,
    level: grant.accessLevel,
    expiresAt: grant.expiresAt ?? undefined,
    isMissing: grant.scopeNodeExists === false,
  };
};

export const toRequestedGrant = (grant: IAccessGrant): IRequestedGrant => ({
  accessLevel: grant.level,
  scopeNodeId: grant.identity,
  expiresAt: grant.expiresAt ?? null,
});

export const toOrgUser = (user: IOrganizationUserView): IOrgUser => ({
  identity: user.userId,
  firstName: user.firstName,
  lastName: user.lastName,
  name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
  email: user.email || undefined,
  mobile: user.mobileNumber || undefined,
  role: user.role,
  status: user.status,
  grants: (user.grants ?? []).map((g) => toAccessGrant(g)),
});
